// Swarthmore College: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Swarthmore's Common Data Set 2025-2026 (Swarthmore-CDS-2025-26.pdf),
//    linked from the Institutional Effectiveness, Research & Assessment
//    Common Data Set page: C1 applicants, admits and enrollees for Fall 2025
//    (the C1 residency table is blank), C8 test policy for Fall 2027, C21/C22
//    early plans. Its G1/G5 costs are marked as 2025-2026 figures (the
//    2026-2027 costs were "not available at this time"), so they are not used.
//  - Swarthmore Admissions: Application Materials & Deadlines (the page names
//    "the 2026-2027 admissions cycle"), Standardized Testing Policy, Art
//    Supplements. The deadlines page gives decision release times, not dates.
//    The admissions pages do not spell out the early decision commitment, so
//    the binding note rests on the CDS C21 definition Swarthmore answered yes
//    to, plus the Early Decision Agreement the deadlines page requires.
//  - Swarthmore Financial Aid, Tuition, Books, Housing & Food Plan,
//    Activities Fee: College billed charges and non-billed charges for
//    2026-2027 and the total cost of attendance ($98,370, which equals the
//    sum of the items used). Transportation and student health insurance are
//    listed as "Varies" and left out of the rows. The same page and the
//    Swarthmore Tuition Guarantee page announce the guarantee that starts in
//    fall 2027.
//  - College Scorecard, school id 216287 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// No announced change for fall 2028 entry was found on the admissions pages.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Swarthmore Common Data Set 2025-2026',
        url: 'https://www.swarthmore.edu/institutional-effectiveness-research-assessment/common-data-set',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Swarthmore Admissions, Application Materials & Deadlines',
        url: 'https://www.swarthmore.edu/admissions-aid/application-materials-deadlines',
        read: '2026-10-01',
    },
    tests: {
        label: 'Swarthmore Admissions, Standardized Testing Policy',
        url: 'https://www.swarthmore.edu/admissions-aid/standardized-testing-policy',
        read: '2026-10-01',
    },
    art: {
        label: 'Swarthmore Admissions, Art Supplements',
        url: 'https://www.swarthmore.edu/admissions-aid/art-supplements',
        read: '2026-10-01',
    },
    costs: {
        label: 'Swarthmore Financial Aid, Tuition, Books, Housing & Food Plan, Activities Fee',
        url: 'https://www.swarthmore.edu/financial-aid/tuition-books-housing-food-plan-activities-fee',
        read: '2026-10-01',
    },
    guarantee: {
        label: 'Swarthmore Financial Aid, Swarthmore Tuition Guarantee',
        url: 'https://www.swarthmore.edu/financial-aid/swarthmore-tuition-guarantee',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?216287-Swarthmore-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'swarthmore-college',
    kbId: 'swarthmore_college',
    name: 'Swarthmore College',
    shortName: 'Swarthmore',
    aliases: ['Swat'],
    location: 'Swarthmore, PA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Swarthmore athletics colors); decoration only.
    branding: {
        primary: '#9D2235',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/swarthmore_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 12995,
        admitted: 977,
        enrolled: 426,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Swarthmore\'s Common Data Set reports one first-year acceptance rate for the whole college. Students choose a major in the sophomore year, so there is no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision (fall and winter rounds together)', applied: 1281, admitted: 228 },
        ],
        note: 'Early decision counts for the Fall 2025 entering class, as the Common Data Set reports them, with both early decision rounds counted together. They are part of the totals above. Swarthmore has no early action plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Swarthmore as a whole and choose a major later. One optional addition:',
        rows: [
            { school: 'Arts supplement (optional)', requirement: 'Students with strong backgrounds in creative writing, dance, music, theater or the visual arts may share samples of their work through the applicant portal after applying, free of charge. Submissions are subject to faculty review, and Swarthmore cannot guarantee all works will be evaluated. Due November 19 for Fall Early Decision, January 8 for Winter Early Decision and January 10 for Regular Decision.', source: 'art' },
        ],
        source: 'art',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Fall Early Decision', deadline: 'November 15', decision: 'Mid-December', binding: true },
            { plan: 'Winter Early Decision', deadline: 'January 4', decision: 'Mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'By April 1', binding: false },
        ],
        note: 'You may apply through the Common App, Coalition on Scoir or QuestBridge, with no preference among them; the application fee is $60 or a fee waiver. Early decision applicants also send first quarter or trimester senior grades. Swarthmore asks that testing be done by early November for Fall Early Decision and by mid-December for Winter Early Decision.',
        source: 'deadlines',
        binding: 'Early decision is a commitment: Swarthmore offers early decision as the Common Data Set defines it, a plan that asks you to commit to attending if admitted, and early decision applicants send a signed Early Decision Agreement through the Common App, Coalition on Scoir or QuestBridge.',
        bindingSource: 'cds',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Sending SAT or ACT scores is optional, and you are not penalized if you do not. You choose on the application whether your scores are considered or suppressed, and that choice cannot be changed after you apply. Swarthmore superscores the SAT but not the ACT, accepts scores you enter yourself in place of official ones, and does not consider the writing sections.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, and says Swarthmore has suspended its testing requirement for the 2026-27 admissions cycle.',
        cdsWhere: 'section C8',
    },

    currentChanges: [
        { topic: 'Cost', line: 'Starting in fall 2027, Swarthmore covers at least the cost of tuition for domestic students whose families have a household income of $200,000 or less and typical assets. You qualify by completing the financial aid application by the posted deadlines.', source: 'guarantee' },
    ],

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition and books', amount: 72722 },
            { item: 'Student activities fee', amount: 482 },
            { item: 'Housing (on campus)', amount: 11676 },
            { item: 'Food (on campus)', amount: 10890 },
            { item: 'Course materials, supplies and equipment', amount: 825 },
            { item: 'Personal expenses', amount: 1775 },
        ],
        total: 98370,
        note: 'Swarthmore\'s total cost of attendance for the year. Tuition includes $800 a year for course materials at the campus store. Transportation and student health insurance vary and are not included.',
        source: 'costs',
        netPrice: {
            average: 17392,
            byIncome: [
                { income: '$0 to $30,000', amount: 6966 },
                { income: '$30,001 to $48,000', amount: 7978 },
                { income: '$48,001 to $75,000', amount: 18171 },
                { income: '$75,001 to $110,000', amount: 14741 },
                { income: 'Over $110,000', amount: 50329 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Swarthmore\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
